import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  OutlinedInput,
  Typography,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ACADEMIC_YEARS, SUBJECTS, USER_PROFILE } from '../config/Config';
import { filterPublished } from '../utils/publishedFilter';

const ProfileBuilder = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [years, setYears] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [year, setYear] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  // Prevent Parent users from accessing profile builder
  const isParent = user?.isParent || user?.is_parent || (user?.role_name && user?.role_name.toString().toLowerCase() === 'parent');
  
  useEffect(() => {
    // Redirect Parent users to dashboard
    if (isParent) {
      navigate('/dashboard', { replace: true });
      return;
    }
  }, [isParent, navigate]);

  useEffect(() => {
    // Don't fetch data if user is a Parent
    if (isParent) return;
    
    const fetchLOVs = async () => {
      try {
        const [yearsRes, subjectsRes] = await Promise.all([
          api.get(ACADEMIC_YEARS + '?published_only=true'),
          api.get(SUBJECTS)
        ]);
        // The API might return either an array or an object with a `results` key (e.g. DRF pagination)
        const yrs = Array.isArray(yearsRes.data) ? yearsRes.data : yearsRes.data?.results ?? [];
        const subs = Array.isArray(subjectsRes.data) ? subjectsRes.data : subjectsRes.data?.results ?? [];
        setYears(filterPublished(yrs));
        setSubjects(subs);
      } catch (e) {
        console.error('Failed loading LOVs', e);
        setSnack({ open: true, msg: 'Failed to load options', sev: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchLOVs();
  }, [isParent]);

  const handleSave = async () => {
    if (!year) {
      setSnack({ open: true, msg: 'Please select academic year', sev: 'warning' });
      return;
    }
    if (selectedSubjects.length === 0) {
      setSnack({ open: true, msg: 'Please select at least one subject', sev: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        academic_year_id: parseInt(year, 10),
        subjects_ids: selectedSubjects.map((id) => parseInt(id, 10))
      };
      await api.patch(USER_PROFILE, payload);
      
      // Refresh user data
      const profileRes = await api.get(USER_PROFILE);
      const updatedUser = profileRes.data;
      setSnack({ open: true, msg: 'Profile saved', sev: 'success' });
      // Persist token and user permanently now that profile is complete
      const existingToken = sessionStorage.getItem('token') || localStorage.getItem('token');
      if (existingToken) {
        localStorage.setItem('token', existingToken);
      }
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      // redirect immediately
      navigate('/dashboard', { replace: true });
    } catch (e) {
      console.error('Save profile error', e.response?.data || e.message);
      const msg = e.response?.data?.message || e.response?.data?.detail || 'Save failed';
      setSnack({ open: true, msg, sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Redirect once profile complete and context has updated
  useEffect(() => {
    // Don't redirect if user is a Parent
    if (isParent) return;
    
    if (user?.academic_year && Array.isArray(user?.subjects) && user.subjects.length > 0) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate, isParent]);


  // Don't render anything if user is a Parent
  if (isParent) return null;
  
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <Card sx={{ width: 400 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>Complete Your Profile</Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel id="year-label">Academic Year</InputLabel>
            <Select
              labelId="year-label"
              value={year}
              label="Academic Year"
              onChange={(e) => setYear(e.target.value)}
            >
              {Array.isArray(years) && years.map((y) => (
                <MenuItem key={y.id} value={y.id}>{y.year ?? y.name ?? y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel id="subjects-label">Subjects</InputLabel>
            <Select
              labelId="subjects-label"
              multiple
              value={selectedSubjects}
              onChange={(e) => setSelectedSubjects(e.target.value)}
              input={<OutlinedInput label="Subjects" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const subj = subjects.find((s) => s.id === id);
                    return <Chip key={id} label={subj ? subj.name : id} />;
                  })}
                </Box>
              )}
            >
              {Array.isArray(subjects) && subjects.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name ?? s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" fullWidth sx={{ mt: 2 }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </CardContent>
      </Card>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfileBuilder;
